import { AlertCircle, Expand, RefreshCw } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from '../ui/button'

interface PPTXViewerProps {
  fileUrl: string
  onLoadingComplete?: () => void
  className?: string
  isDownloadable?: boolean
}

export default function PPTXViewer({ fileUrl, onLoadingComplete, className, isDownloadable = false }: PPTXViewerProps) {
  const iframeContainerRef = React.useRef<HTMLDivElement>(null)
  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const overlay1Ref = React.useRef<HTMLDivElement>(null)
  const overlay2Ref = React.useRef<HTMLDivElement>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const loadTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const completionCallbackRef = React.useRef(false) // Track if onLoadingComplete has been called
  const observerRef = React.useRef<MutationObserver | null>(null)

  const viewerUrl = React.useMemo(
    () => `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`,
    [fileUrl]
  )

  const handleIframeLoad = React.useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current)
    }

    // For large files, give more time for the viewer to fully render
    // Office viewer needs time to process and display the content
    loadTimeoutRef.current = setTimeout(() => {
      setLoading(false)
      setError(null)
      // Call onLoadingComplete after a longer delay to ensure file is fully rendered
      // This prevents cleanup from happening before the presentation is actually viewable
      // Only call once
      if (!completionCallbackRef.current && onLoadingComplete) {
        setTimeout(() => {
          completionCallbackRef.current = true
          onLoadingComplete()
        }, 3000) // Additional 3 seconds after loading state clears to ensure rendering is complete
      }
    }, 3000) // 3 seconds for iframe load
  }, [onLoadingComplete])

  // Set up loading timeout for very large files
  React.useEffect(() => {
    // Set a maximum timeout of 30 seconds for large files
    const maxTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false)
        // Don't set error, just stop loading - file might still be loading in background
      }
    }, 30000)

    return () => {
      clearTimeout(maxTimeout)
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current)
      }
    }
  }, [loading])

  // Note: Direct DOM manipulation of cross-origin iframes is blocked by CORS
  // Solution: Use CSS cropping (top toolbar) + transparent overlays (bottom menu buttons)
  // Tab will close automatically if overlays are removed or tampered with

  // Inject CSS with !important to protect overlays
  React.useEffect(() => {
    const styleId = 'pptx-overlay-protection'
    if (document.getElementById(styleId)) return

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      [data-overlay="menu-blocker-1"],
      [data-overlay="menu-blocker-2"] {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        position: absolute !important;
        z-index: 15 !important;
      }
    `
    document.head.appendChild(style)

    // Monitor if the style element is removed or modified
    const originalStyleContent = style.textContent
    const styleObserver = new MutationObserver(() => {
      const styleElement = document.getElementById(styleId)
      if (!styleElement || !styleElement.isConnected) {
        // Style was removed - close tab
        window.close()
        return
      }
      // Check if content was modified
      if (styleElement.textContent !== originalStyleContent) {
        window.close()
        return
      }
      // Check for new stylesheets that might override our styles
      const allStyles = document.querySelectorAll('style, link[rel="stylesheet"]')
      // If new stylesheets are added after our protection style, it might be to override it
      let foundOurStyle = false
      allStyles.forEach(s => {
        if (s.id === styleId) {
          foundOurStyle = true
        } else if (foundOurStyle) {
          // New stylesheet added after ours - might be trying to override
          // Check if it contains rules targeting our overlays
          if (s.textContent?.includes('menu-blocker') || s.textContent?.includes('data-overlay')) {
            window.close()
          }
        }
      })
    })

    styleObserver.observe(document.head, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => {
      styleObserver.disconnect()
      const existingStyle = document.getElementById(styleId)
      if (existingStyle && existingStyle.parentNode) {
        try {
          existingStyle.parentNode.removeChild(existingStyle)
        } catch (error) {
          // Silently handle if already removed
        }
      }
    }
  }, [])

  // Monitor overlays and close tab if they're removed or tampered with
  React.useEffect(() => {
    if (loading || !iframeContainerRef.current) return

    const container = iframeContainerRef.current
    let closeTimeout: NodeJS.Timeout | null = null
    let hasClosed = false

    const closeTab = () => {
      // Prevent multiple close attempts
      if (hasClosed) return
      hasClosed = true

      if (closeTimeout) clearTimeout(closeTimeout)
      closeTimeout = setTimeout(() => {
        try {
          // Try to close the window/tab
          window.close()
          // If window.close() doesn't work (some browsers block it), try to navigate away
          setTimeout(() => {
            window.location.href = 'about:blank'
          }, 100)
        } catch (error) {
          // If closing fails, navigate to blank page
          window.location.href = 'about:blank'
        }
      }, 200) // Small delay to detect the removal
    }

    // Detect keyboard shortcuts for DevTools
    const handleKeyDown = (e: KeyboardEvent) => {
      // F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U (view source)
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault()
        e.stopPropagation()
        closeTab()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true) // Use capture phase

    const checkOverlayStyles = () => {
      const overlay1 = overlay1Ref.current
      const overlay2 = overlay2Ref.current

      if (!overlay1 || !overlay2) {
        closeTab()
        return
      }

      // Check computed styles to detect CSS hiding
      const checkOverlay = (overlay: HTMLElement) => {
        if (!overlay.isConnected) {
          return true // Overlay removed
        }

        const computed = window.getComputedStyle(overlay)
        const inlineStyle = overlay.style

        // Check if overlay is hidden via CSS
        const isHidden =
          computed.display === 'none' ||
          computed.visibility === 'hidden' ||
          parseFloat(computed.opacity) === 0 ||
          computed.pointerEvents === 'none' ||
          parseFloat(computed.zIndex) < 15 ||
          inlineStyle.display === 'none' ||
          inlineStyle.visibility === 'hidden' ||
          inlineStyle.opacity === '0' ||
          overlay.classList.contains('hidden') ||
          overlay.classList.contains('d-none') ||
          overlay.hasAttribute('hidden')

        // Check if position was changed (moved off screen)
        const position = computed.position
        const bottom = computed.bottom
        const right = computed.right
        const width = computed.width
        const height = computed.height
        const isMovedOffScreen =
          position === 'fixed' ||
          (position === 'absolute' && (bottom === 'auto' || right === 'auto')) ||
          width === '0px' ||
          height === '0px' ||
          parseFloat(width) === 0 ||
          parseFloat(height) === 0

        // Check if z-index was lowered (moved behind other elements)
        const zIndex = parseFloat(computed.zIndex)
        const isBehind = !isNaN(zIndex) && zIndex < 15

        return isHidden || isMovedOffScreen || isBehind
      }

      if (checkOverlay(overlay1) || checkOverlay(overlay2)) {
        closeTab()
      }
    }

    const checkAndClose = () => {
      // Check if overlays exist and are connected to DOM
      const overlay1Exists = overlay1Ref.current?.isConnected
      const overlay2Exists = overlay2Ref.current?.isConnected

      if (!overlay1Exists || !overlay2Exists) {
        // Overlays were removed - close the tab
        closeTab()
        return
      }

      // Also check if overlays are hidden via CSS
      checkOverlayStyles()
    }

    // Create observer to watch for overlay removal or modification
    observerRef.current = new MutationObserver(mutations => {
      // Check if mutations involve our overlay elements
      const relevantMutations = mutations.filter(mutation => {
        // Check for removed nodes
        const removed = Array.from(mutation.removedNodes).some(
          node => node === overlay1Ref.current || node === overlay2Ref.current
        )
        // Check for attribute changes (like style/class modifications)
        const modified =
          mutation.type === 'attributes' &&
          (mutation.target === overlay1Ref.current || mutation.target === overlay2Ref.current)
        return removed || modified
      })
      if (relevantMutations.length > 0) {
        checkAndClose()
      }
    })

    // Observe the container for child removals and attribute changes
    try {
      observerRef.current.observe(container, {
        childList: true,
        subtree: false, // Only watch direct children to avoid conflicts
        attributes: true, // Watch for attribute changes
        attributeFilter: ['style', 'class', 'data-overlay'], // Only watch relevant attributes
      })

      // Also observe the overlays directly for attribute changes
      if (overlay1Ref.current) {
        observerRef.current.observe(overlay1Ref.current, {
          attributes: true,
          attributeFilter: ['style', 'class'], // Watch style and class changes
          attributeOldValue: true, // Track old values
        })
      }
      if (overlay2Ref.current) {
        observerRef.current.observe(overlay2Ref.current, {
          attributes: true,
          attributeFilter: ['style', 'class'], // Watch style and class changes
          attributeOldValue: true, // Track old values
        })
      }
    } catch (error) {
      // Silently handle observation errors
    }

    // Check periodically as a backup - also checks computed styles
    const interval = setInterval(() => {
      checkAndClose()
      checkOverlayStyles() // Check CSS hiding separately
    }, 300) // More frequent checks to catch CSS changes quickly

    return () => {
      if (observerRef.current) {
        try {
          observerRef.current.disconnect()
        } catch (error) {
          // Silently handle disconnection errors
        }
      }
      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
      clearInterval(interval)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [loading])

  const handleFullscreen = () => {
    if (iframeContainerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        iframeContainerRef.current.requestFullscreen()
      }
    }
  }

  const handleRetry = React.useCallback(() => {
    setError(null)
    setLoading(true)
    completionCallbackRef.current = false // Reset completion flag on retry
    if (iframeRef.current) {
      iframeRef.current.src = viewerUrl
    }
  }, [viewerUrl])

  // onLoadingComplete is now called directly from handleIframeLoad
  // No need for separate useEffect

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="font-semibold mb-2 text-red-600">Error loading PPTX file</p>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <div className="flex gap-2">
            <Button onClick={handleRetry} variant="outline">
              <RefreshCw className="size-4 mr-2" />
              Retry
            </Button>
            {isDownloadable && (
              <a
                href={fileUrl}
                download
                className="flex items-center gap-2 py-2 px-4 border border-black bg-white text-black rounded hover:bg-gray-200 transition"
              >
                Download File
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('relative w-full h-screen', className)} ref={iframeContainerRef}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading presentation...</p>
            <p className="text-sm text-gray-500 mt-2">Large files may take longer to load</p>
          </div>
        </div>
      )}
      {/* Use CSS cropping to hide top toolbar buttons */}
      <div className="w-full h-full overflow-hidden">
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          className="w-full"
          style={{
            height: '100%',
            outline: 'none',
            border: 'none',
          }}
          frameBorder="0"
          onLoad={handleIframeLoad}
          title="PPTX Viewer"
          allow="fullscreen"
        >
          This browser does not support PPTX files.
        </iframe>
      </div>

      {/* Comprehensive transparent overlays to block all menu buttons */}
      {/* Using refs and inline styles with !important to make removal harder */}
      {!loading && (
        <>
          {/* Bottom-right corner overlay - blocks menu button and dropdown */}
          <div
            ref={overlay1Ref}
            data-overlay="menu-blocker-1"
            className="absolute"
            style={{
              bottom: '0px',
              right: '0px',
              width: 'min(350px, 30vw)',
              height: '60px',
              backgroundColor: 'transparent',
              zIndex: 15,
              cursor: 'default',
              pointerEvents: 'auto',
            }}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onMouseDown={e => {
              e.preventDefault()
              e.stopPropagation()
              // Close tab on right-click (inspect element)
              if (e.button === 2) {
                window.close()
              }
            }}
            onContextMenu={e => {
              e.preventDefault()
              e.stopPropagation()
              // Close tab on right-click
              window.close()
            }}
            onMouseUp={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onMouseEnter={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            aria-hidden="true"
          />
          {/* Additional overlay for menu dropdown that might appear above */}
          <div
            ref={overlay2Ref}
            data-overlay="menu-blocker-2"
            className="absolute"
            style={{
              bottom: '60px',
              right: '0px',
              width: 'min(250px, 25vw)',
              height: '200px',
              backgroundColor: 'transparent',
              zIndex: 15,
              cursor: 'default',
              pointerEvents: 'auto',
            }}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onMouseDown={e => {
              e.preventDefault()
              e.stopPropagation()
              // Close tab on right-click (inspect element)
              if (e.button === 2) {
                window.close()
              }
            }}
            onContextMenu={e => {
              e.preventDefault()
              e.stopPropagation()
              // Close tab on right-click
              window.close()
            }}
            onMouseUp={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            aria-hidden="true"
          />
        </>
      )}

      <Button
        onClick={handleFullscreen}
        className="absolute top-2 right-2 bg-gray-600 text-white p-2 rounded transition hover:bg-gray-700 z-20"
        size="icon"
        variant="ghost"
        aria-label="Toggle fullscreen"
      >
        <Expand className="size-6" />
      </Button>
    </div>
  )
}
